import { useCallback, useMemo, useState } from 'react';
import FiltersComponent, { IOnFilterChangeProps, IFiltersProps } from './Filters/Filters';
import { TOnChangeParams } from '../ComposedView/ComposedView';

interface IUseFiltersStatefulProps {
  /** Callback to close the drawer */
  onClose(): void;
  /** Callback used to set new data state. Should return the new state of the filters */
  onChange(params: TOnChangeParams): void;
  filterValues?: IFiltersProps['filterValues'];
}

export type TFiltersStatefulProps = Partial<Omit<IFiltersProps, 'filterables'>> &
  Required<Pick<IFiltersProps, 'filterables'>> &
  IUseFiltersStatefulProps;

export const useFiltersStateful = ({
  filterValues,
  onChange,
  onClose,
}: IUseFiltersStatefulProps) => {
  const [localFilters, setLocalFilters] = useState(filterValues);

  const onApply = useCallback(() => {
    onChange({
      name: 'filters',
      value: localFilters,
    });
    onClose();
  }, [localFilters, onChange, onClose]);

  const onFilterChange = useCallback((filterParams: IOnFilterChangeProps) => {
    const { filterable, value } = filterParams;
    const { componentType, field } = filterable;
    const newFilter = {
      ...value,
      filterType: componentType,
    };
    setLocalFilters((currentFilters) => ({
      ...currentFilters,
      [field]: newFilter,
    }));
  }, []);

  const onClear = useCallback((name: string) => {
    setLocalFilters((currentFilters) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const newFilter = { ...currentFilters };
      delete newFilter[name];
      return newFilter;
    });
  }, []);

  const onCancel = useCallback(() => {
    setLocalFilters(filterValues);
    onClose();
  }, [filterValues, onClose]);

  return useMemo(
    () => ({
      onApply,
      onCancel,
      onClear,
      onFilterChange,
      filterValues: localFilters,
    }),
    [localFilters, onApply, onCancel, onClear, onFilterChange]
  );
};

const FiltersStateful = ({ filterValues, onChange, onClose, ...props }: TFiltersStatefulProps) => {
  const propsFromHook = useFiltersStateful({ filterValues, onChange, onClose });
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <FiltersComponent {...propsFromHook} {...props} />;
};

export default FiltersStateful;
